import {isVacationResponderValid} from '../../../helpers/vacationResponderHelpers';
import {serializeAttachment} from '../../../models/api/attachmentModel';
import {ApiVacationResponderDataGroup} from '../../../models/api/dataGroupModel';
import {ApiVacationResponder, VacationResponderModel} from '../../../models/api/vacationResponderModel';
import {deleteAsync, postApiAsync, putApiAsync} from '../../../services/http/http';
import {findVacationResponderQueue} from '../../../services/tasks/specializedTaskQueues';
import {ThunkAsyncAction} from '../../appThunk';
import {getCompany, getCompanyUrl} from '../company/companySelectors';

export function createVacationResponderAsyncAction(template: VacationResponderModel): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const company = getCompany(getState());
    const url = `${company.url}/vacation_responders`;
    const body = {
      namespace: template.namespace,
      name: template.name,
      body: template.body,
      enabled: template.isEnabled,
      only_contacts: template.onlyContacts,
      start_at: template.startAt,
      end_at: template.endAt,
      attachments: template.attachments.map((a) => serializeAttachment(a)),
      channel_ids: template.channelIds,
    };

    const vacationResponder = await postApiAsync(ApiVacationResponder, url, body);

    // Add to the store.
    const queue = findVacationResponderQueue(dispatch, getState, vacationResponder.id);
    await queue.waitForTurnAsync();
    await queue.provideResultAsync({vacationResponders: [vacationResponder]});
  };
}

export function saveVacationResponderAsyncAction(
  id: number,
  newResponder: Partial<VacationResponderModel>,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const queue = findVacationResponderQueue(dispatch, getState, id);
    const vacationResponder = queue.get();
    if (!vacationResponder) {
      throw new Error('The vacation responder to save could not be found.');
    }

    const updatedResponder = {...vacationResponder, ...newResponder};

    if (!isVacationResponderValid(updatedResponder)) {
      throw new Error('This vacation responder is invalid and cannot be saved.');
    }

    // Update locally.
    queue.update({vacationResponders: [updatedResponder]});

    await queue.waitForTurnAsync();

    // Update remotely.
    const company = getCompany(getState());
    const url = `${company.url}/vacation_responders/${id}`;
    const body = {
      name: updatedResponder.name,
      body: updatedResponder.body,
      enabled: updatedResponder.isEnabled,
      only_contacts: updatedResponder.onlyContacts,
      start_at: updatedResponder.startAt,
      end_at: updatedResponder.endAt,
      attachments: updatedResponder.attachments.map((a) => serializeAttachment(a)),
      channel_ids: updatedResponder.channelIds,
    };

    try {
      const response = await putApiAsync(ApiVacationResponderDataGroup, url, body);
      await queue.provideResultAsync(response);
    } catch (error) {
      queue.reportError();
      throw error;
    }
  };
}

export function deleteVacationResponderAsyncAction(vacationResponderId: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const queue = findVacationResponderQueue(dispatch, getState, vacationResponderId);

    // Delete locally.
    queue.delete();
    await queue.waitForTurnAsync();

    // Delete remotely.
    const companyUrl = getCompanyUrl(getState());
    const url = `${companyUrl}/vacation_responders/${vacationResponderId}`;

    try {
      await deleteAsync(url);
      await queue.reportDeleted();
    } catch (error) {
      queue.reportError();
      throw error;
    }
  };
}

export function deleteVacationRespondersBulkAsyncAction(
  vacationResponderIds: ReadonlyArray<number>,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // We have no multi delete endpoint; this issues multiple events.
    vacationResponderIds.forEach((vacationResponderId) =>
      dispatch(deleteVacationResponderAsyncAction(vacationResponderId)),
    );
  };
}
